// The platform's officially supported UI languages (see
// `features/platform-i18n.md`). This is the single source of truth for the
// language codes: the i18n layer (`src/i18n/messages.ts`) derives its `Locale`
// type from here, a company's `defaultLanguage` is validated against it on the
// server, and Postgres stores the code as plain text narrowed back through
// `toLanguageCode` on read.
//
// Pure TS with zero imports — the domain layer is shared by the client and the
// server, so the message catalogues themselves (JSON) live outside it.

/** ISO 639-1 code of a supported UI language. */
export type LanguageCode = 'en' | 'zh' | 'ru' | 'km';

/** Every supported language, in display order. */
export const SUPPORTED_LANGUAGES: readonly LanguageCode[] = [
  'en',
  'zh',
  'ru',
  'km',
];

/** The platform-wide fallback language (also a new company's default). */
export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** Whether `value` is a supported language code. */
export function isLanguageCode(value: unknown): value is LanguageCode {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}

/**
 * Narrow an arbitrary value to a supported `LanguageCode`, or `null`. A BCP-47
 * tag matches by its primary subtag, so a device locale like `zh-Hans-CN` or
 * `ru-RU` resolves to its supported base language.
 */
export function toLanguageCode(value: unknown): LanguageCode | null {
  if (typeof value !== 'string') return null;
  const base = value.toLowerCase().split('-')[0];
  return isLanguageCode(base) ? base : null;
}
